import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CameraIcon, CheckIcon, IdCardIcon, MapPinIcon, PlusIcon, SendIcon, ShieldCheckIcon, UploadIcon, UserRoundIcon, UsersRoundIcon, XIcon, BoxIcon } from "lucide-react";
import { editableFields, familyMembers, memberValues, verifiedAddresses, FamilyMember } from "../../data/carte";
const ease = [0.23, 1, 0.32, 1] as const;
const fieldIcons: Record<string, BoxIcon> = {
  prenom: UserRoundIcon,
  nom: IdCardIcon,
  genre: UsersRoundIcon,
  photo: CameraIcon,
  adresse: MapPinIcon
};
interface EditCardDialogProps {
  member: FamilyMember | null;
  onClose: () => void;
}
type Step = 'champs' | 'formulaire' | 'preuves' | 'confirmation';
export function EditCardDialog({
  member,
  onClose
}: EditCardDialogProps) {
  const [step, setStep] = useState<Step>('champs');
  const [selected, setSelected] = useState<string[]>([]);
  const [alsoFor, setAlsoFor] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [addressChoice, setAddressChoice] = useState<string>(verifiedAddresses[0].id);
  const [newAddress, setNewAddress] = useState('');
  const [proofs, setProofs] = useState<string[]>([]);
  const current = member ? memberValues[member.id] ?? {} : {};
  useEffect(() => {
    if (member) {
      setStep('champs');
      setSelected([]);
      setAlsoFor([]);
      setValues(memberValues[member.id] ?? {});
      setAddressChoice(verifiedAddresses[0].id);
      setNewAddress('');
      setProofs([]);
    }
  }, [member]);
  if (!member) return null;
  const chosen = editableFields.filter(field => selected.includes(field.id));
  const addressSelected = selected.includes('adresse');
  const newAddressChosen = addressSelected && addressChoice === 'nouvelle';
  const needProof = [...chosen.filter(field => field.requiresProof), ...(newAddressChosen ? [editableFields.find(field => field.id === 'adresse')!] : [])];
  const others = familyMembers.filter(item => item.id !== member.id);
  const toggle = (list: string[], id: string) => list.includes(id) ? list.filter(item => item !== id) : [...list, id];
  const next = () => {
    if (step === 'champs') setStep('formulaire');else if (step === 'formulaire') setStep('preuves');else setStep('confirmation');
  };
  const back = () => {
    if (step === 'formulaire') setStep('champs');else if (step === 'preuves') setStep('formulaire');else onClose();
  };
  const stepIndex = ['champs', 'formulaire', 'preuves'].indexOf(step);
  return <Shell title={`Modifier la carte${member.id === 'charlotte' ? '' : ` de ${member.firstName}`}`} onClose={onClose} data-id="element-688" data-mp="ez1rkei1s9ho">
      {step !== 'confirmation' && <ol className="mb-6 flex items-center gap-2" aria-label="Étapes" data-id="element-689" data-mp="50chh7we7tbz">
          {['Champs', 'Informations', 'Validation'].map((label, index) => <li key={label} className="flex flex-1 items-center gap-2" data-id="element-690" data-mp="2uqppod8ss822" data-mpi={index}>
              <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold ${index <= stepIndex ? 'bg-ville-indigo text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-691" data-mp="2pq1z1sohdt8d">
                {index + 1}
              </span>
              <span className={`text-xs font-semibold ${index <= stepIndex ? 'text-ville-navy' : 'text-slate-400'}`} data-id="element-692" data-mp="2km96b9w408ti">
                {label}
              </span>
            </li>)}
        </ol>}

      <AnimatePresence mode="wait" data-id="element-693" data-mp="2uccam68ol975">
        <motion.div key={step} initial={{
        opacity: 0,
        x: 16
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -16
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-694" data-mp="2uddosjkzubt3">
          {step === 'champs' && <div data-id="element-695" data-mp="38c3jxto961et">
              <p className="text-sm text-slate-600" data-id="element-696" data-mp="389hd5w96rpxn">Quels renseignements souhaitez-vous modifier ?</p>

              <ul className="mt-4 grid grid-cols-2 gap-3" data-id="element-697" data-mp="3d82czukj07lk">
                {editableFields.map(field => {
              const Icon = fieldIcons[field.id];
              const active = selected.includes(field.id);
              return <li key={field.id} data-id="element-698" data-mp="39d94xu6uga4i">
                      <button type="button" onClick={() => setSelected(list => toggle(list, field.id))} aria-pressed={active} className={`relative flex h-full w-full flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-699" data-mp="39c6bbei4ztkw">
                        <span className={`grid h-9 w-9 place-items-center rounded-xl ${active ? 'bg-ville-indigo text-white' : 'bg-slate-100 text-ville-navy'}`} aria-hidden="true" data-id="element-700" data-mp="2lnwy30yzoe5d">
                          <Icon className="h-4 w-4" data-id="element-701" data-mp="2zheh8knrjouv" />
                        </span>
                        <span className="text-[15px] font-bold text-ville-navy" data-id="element-702" data-mp="2una7xur0z4qm">{field.label}</span>
                        <span className="text-xs text-slate-500" data-id="element-703" data-mp="26x7jrpq8eixa">{current[field.id]}</span>
                        {active && <span className="absolute right-3 top-3 grid h-5 w-5 place-items-center rounded-full bg-ville-indigo text-white" aria-hidden="true" data-id="element-704" data-mp="21znulurzwlqk">
                            <CheckIcon className="h-3.5 w-3.5" data-id="element-705" data-mp="m8va2jl1wk6o" />
                          </span>}
                      </button>
                    </li>;
            })}
              </ul>
            </div>}

          {step === 'formulaire' && <div className="space-y-4" data-id="element-706" data-mp="dgteaylxfs2q">
              {chosen.filter(field => field.id !== 'adresse').map(field => <div key={field.id} data-id="element-707" data-mp="116dx9hi2ypc0">
                    <label htmlFor={`champ-${field.id}`} className="block text-sm font-bold text-ville-navy" data-id="element-708" data-mp="157hcc1sb0hc6">
                      {field.label}
                    </label>
                    <input id={`champ-${field.id}`} type="text" value={values[field.id] ?? ''} onChange={event => setValues({
              ...values,
              [field.id]: event.target.value
            })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-[15px] text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-709" data-mp="r9st0ydvxi98" />
                    <p className="mt-1 text-xs text-slate-500" data-id="element-710" data-mp="2sssinvhktzmb">Valeur actuelle : {current[field.id]}</p>
                  </div>)}

              {addressSelected && <fieldset data-id="element-711" data-mp="2o10yq3sxes55">
                  <legend className="text-sm font-bold text-ville-navy" data-id="element-712" data-mp="2o09691umlaj3">Adresse</legend>
                  <ul className="mt-2 space-y-2" data-id="element-713" data-mp="2j1enu10klnzi">
                    {verifiedAddresses.map(address => {
                const active = addressChoice === address.id;
                return <li key={address.id} data-id="element-714" data-mp="294jo5esk9hjn">
                          <button type="button" onClick={() => setAddressChoice(address.id)} aria-pressed={active} className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-715" data-mp="253q5p0xw0uy5">
                            <MapPinIcon className={`mt-0.5 h-5 w-5 shrink-0 ${active ? 'text-ville-indigo' : 'text-slate-400'}`} aria-hidden="true" data-id="element-716" data-mp="5xazjs6b477v" />
                            <span className="min-w-0" data-id="element-717" data-mp="au1gehpxr6uc">
                              <span className="block text-[15px] font-semibold text-ville-navy" data-id="element-718" data-mp="fmcdzdx7egjq">{address.label}</span>
                              <span className="block text-xs text-slate-500" data-id="element-719" data-mp="kkdwnxqpwduo">{address.note}</span>
                            </span>
                          </button>
                        </li>;
              })}

                    <li data-id="element-720" data-mp="3o2xrn80s5xt6">
                      <button type="button" onClick={() => setAddressChoice('nouvelle')} aria-pressed={newAddressChosen} className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${newAddressChosen ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-dashed border-slate-300 hover:border-ville-indigo'}`} data-id="element-721" data-mp="3o4idm99148ff">
                        <PlusIcon className={`h-5 w-5 shrink-0 ${newAddressChosen ? 'text-ville-indigo' : 'text-slate-400'}`} aria-hidden="true" data-id="element-722" data-mp="1ydlzyfxxh1ca" />
                        <span className="text-[15px] font-semibold text-ville-navy" data-id="element-723" data-mp="1td7p6q4xzdak">Ajouter une nouvelle adresse</span>
                      </button>
                    </li>
                  </ul>

                  {newAddressChosen && <div className="mt-3" data-id="element-724" data-mp="2h4wmkkwmeryd">
                      <label htmlFor="nouvelle-adresse" className="block text-sm font-bold text-ville-navy" data-id="element-725" data-mp="2h3vbwhjio7h6">
                        Nouvelle adresse (numéro, rue, municipalité)
                      </label>
                      <input id="nouvelle-adresse" type="text" value={newAddress} onChange={event => setNewAddress(event.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-[15px] text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-726" data-mp="2r1rlh4wt2ud6" />
                      <p className="mt-1 text-xs text-slate-500" data-id="element-727" data-mp="33cs9qbf3vacj">
                        Une nouvelle adresse doit être vérifiée par la Ville à l’étape suivante.
                      </p>
                    </div>}

                  <div className="mt-4 rounded-2xl bg-slate-50 px-5 py-4" data-id="element-728" data-mp="2toad86duzpep">
                    <p className="text-sm font-bold text-ville-navy" data-id="element-729" data-mp="2ymbvwq7dhmpn">Modifier aussi pour…</p>
                    <ul className="mt-2 space-y-1.5" data-id="element-730" data-mp="2kmu3m0uvbrbj">
                      {others.map(item => {
                  const active = alsoFor.includes(item.id);
                  return <li key={item.id} data-id="element-731" data-mp="2opqqi3rfy9la">
                            <button type="button" onClick={() => setAlsoFor(list => toggle(list, item.id))} aria-pressed={active} disabled={!item.canEdit} className="flex w-full items-center gap-3 rounded-xl px-2 py-1.5 text-left transition-colors duration-150 ease-ville hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent" data-id="element-732" data-mp="2aowfregx7wjg">
                              <span className={`grid h-5 w-5 shrink-0 place-items-center rounded border ${active ? 'border-ville-indigo bg-ville-indigo text-white' : item.canEdit ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-100'}`} aria-hidden="true" data-id="element-733" data-mp="3f6mjs5irhoaz">
                                {active && <CheckIcon className="h-3.5 w-3.5" data-id="element-734" data-mp="3b51b6wd9fw6l" />}
                              </span>
                              <img src={item.photo} alt="" className="h-7 w-7 rounded-full object-cover" data-id="element-735" data-mp="2r2flvu7actm" />
                              <span className="text-sm font-semibold text-ville-navy" data-id="element-736" data-mp="6qb7sw9le2n2">{item.firstName}</span>
                              {!item.canEdit && <span className="ml-auto text-xs text-slate-500" data-id="element-737" data-mp="3ozbq6lzggbzg">Consentement requis</span>}
                            </button>
                          </li>;
                })}
                    </ul>
                  </div>
                </fieldset>}
            </div>}

          {step === 'preuves' && needProof.length === 0 && <div className="text-center" data-id="element-738" data-mp="2axmgae5vm4gn">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-739" data-mp="2awb904d82eks">
                <ShieldCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-740" data-mp="260nkzsah7rgv" />
              </span>
              <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-741" data-mp="2jxj5cgw6h5c8">
                Modifications enregistrées
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-742" data-mp="1r954zuhm9wxi">
                Votre carte est à jour. Les changements sont visibles immédiatement.
              </p>
              {alsoFor.length > 0 && <p className="mt-2 text-sm text-slate-500" data-id="element-743" data-mp="20zq2felk0veu">
                  Appliqué aussi à{' '}
                  {alsoFor.map(id => familyMembers.find(item => item.id === id)?.firstName).filter(Boolean).join(', ')}
                  .
                </p>}
            </div>}

          {step === 'preuves' && needProof.length > 0 && <div data-id="element-744" data-mp="13jatphcy4gfd">
              <p className="text-sm text-slate-600" data-id="element-745" data-mp="13jssolqzt8qz">
                Ces renseignements doivent être validés par la Ville. Joignez un document pour chacun.
              </p>
              <ul className="mt-4 space-y-3" data-id="element-746" data-mp="38k3upjtc0kxj">
                {needProof.map(field => {
              const done = proofs.includes(field.id);
              return <li key={field.id} className="rounded-2xl border border-slate-200 px-5 py-4" data-id="element-747" data-mp="3ib88whb6o1rb">
                      <p className="text-[15px] font-bold text-ville-navy" data-id="element-748" data-mp="2knfz8nuxv82u">{field.label}</p>
                      <p className="mt-0.5 text-sm text-slate-500" data-id="element-749" data-mp="2kpsrbtueuytm">{field.proofLabel}</p>
                      <button type="button" onClick={() => setProofs(list => toggle(list, field.id))} className={`mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${done ? 'bg-ville-indigo-soft text-ville-indigo' : 'border border-slate-200 text-ville-navy hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-750" data-mp="2pon7sp4wobak">
                        {done ? <CheckIcon className="h-4 w-4" data-id="element-751" data-mp="1ydzj3c5sfesx" /> : <UploadIcon className="h-4 w-4" data-id="element-752" data-mp="1yh54agbapwjd" />}
                        {done ? 'Document joint' : 'Joindre un document'}
                      </button>
                    </li>;
            })}
              </ul>
            </div>}

          {step === 'confirmation' && <div className="text-center" data-id="element-753" data-mp="1kmktgmhdkfcw">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-754" data-mp="1kltbiegpag53">
                <ShieldCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-755" data-mp="1aoyfc27wgrtz" />
              </span>
              <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-756" data-mp="16nl5lh1os4hs">
                {needProof.length > 0 ? 'Demande transmise' : 'Modifications enregistrées'}
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-757" data-mp="311m8qe49rdno">
                {needProof.length > 0 ? 'La Ville validera les documents fournis. Vous serez avisé dès que la carte sera mise à jour.' : 'Votre carte est à jour. Les changements sont visibles immédiatement.'}
              </p>
              {alsoFor.length > 0 && <p className="mt-2 text-sm text-slate-500" data-id="element-758" data-mp="1xpx99p76ugbo">
                  Appliqué aussi à{' '}
                  {alsoFor.map(id => familyMembers.find(item => item.id === id)?.firstName).filter(Boolean).join(', ')}
                  .
                </p>}
            </div>}
        </motion.div>
      </AnimatePresence>

      <div className="mt-7 flex items-center justify-between gap-4" data-id="element-759" data-mp="19zum9ni4s0jx">
        {step === 'confirmation' || step === 'preuves' && needProof.length === 0 ? <button type="button" onClick={onClose} className="ml-auto rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-760" data-mp="110zc60byu6bc">
            Terminer
          </button> : <>
            <button type="button" onClick={back} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-761" data-mp="w13aw8unkszh">
              {step === 'champs' ? 'Annuler' : 'Retour'}
            </button>
            <button type="button" onClick={next} disabled={step === 'champs' && selected.length === 0} className="rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:hover:translate-y-0" data-id="element-762" data-mp="mbxo32hgakix">
              {step === 'formulaire' && needProof.length === 0 ? 'Enregistrer' : step === 'preuves' ? 'Transmettre' : 'Continuer'}

            </button>
          </>}
      </div>
    </Shell>;
}
interface ConsentDialogProps {
  member: FamilyMember | null;
  onClose: () => void;
}
export function ConsentDialog({
  member,
  onClose
}: ConsentDialogProps) {
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (member) setSent(false);
  }, [member]);
  if (!member) return null;
  return <Shell title={`Modifier la carte de ${member.firstName}`} onClose={onClose} data-id="element-763" data-mp="1r4z6y6xtzrlj">
      {sent ? <div className="text-center" data-id="element-764" data-mp="1r34metqh21hz">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-765" data-mp="1vvxr5hlxx9u2">
            <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-766" data-mp="1zwrbk106c6i7" />
          </span>
          <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-767" data-mp="24vcew9aq36av">Demande envoyée</h3>
          <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-768" data-mp="29wrz5z52t8s7">
            {member.firstName} recevra une notification et pourra vous autoriser à modifier sa carte.
          </p>
        </div> : <div className="flex items-start gap-4" data-id="element-769" data-mp="fih6tgp9vwho">
          <img src={member.photo} alt="" className="h-14 w-14 shrink-0 rounded-full object-cover" data-id="element-770" data-mp="fjs9teaw907q" />
          <p className="text-[15px] leading-relaxed text-slate-600" data-id="element-771" data-mp="2xqsaredpymvt">
            Vous n’avez pas le consentement de {member.firstName} pour modifier sa carte.
          </p>
        </div>}

      <div className="mt-7 flex items-center justify-between gap-4" data-id="element-772" data-mp="2sxz76ttzlklb">
        <button type="button" onClick={onClose} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-773" data-mp="2oz9t0z14jz0m">
          {sent ? 'Fermer' : 'Annuler'}
        </button>
        {!sent && <button type="button" onClick={() => setSent(true)} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-774" data-mp="3cokoq23mb37w">
            <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-775" data-mp="1rapzut4k4y28" />
            Demander son consentement
          </button>}
      </div>
    </Shell>;
}
function Shell({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease
  }} data-id="element-776" data-mp="7zh8xqvhvra">
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-777" data-mp="dycrj6r8lmjs" />
      <motion.div role="dialog" aria-modal="true" aria-label={title} initial={{
      y: 20,
      opacity: 0,
      scale: 0.98
    }} animate={{
      y: 0,
      opacity: 1,
      scale: 1
    }} exit={{
      y: 12,
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease
    }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-7 shadow-2xl sm:rounded-3xl" data-id="element-778" data-mp="ixpctn2gmlk9">
        <button type="button" onClick={onClose} aria-label="Fermer" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-779" data-mp="11op6h1kwgjjv">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-780" data-mp="2w3cfti2qe5bk" />
        </button>
        <h2 className="pr-10 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-781" data-mp="2r5u97yc6s26q">{title}</h2>
        <div className="mt-5" data-id="element-782" data-mp="23g9n56kqkot8">{children}</div>
      </motion.div>
    </motion.div>;
}